import { FC, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';
import dayjs from 'dayjs';

import { DispatchProperties, useSelector } from '../../../redux/store';
import { deleteCancellationGroup, getPendingRequests } from '../../../redux/State/clientSlice/scheduleCancellationSlice';
import { AdminTypes } from '../../../components/Menu';
import { ORANGE } from '../../../shared/Helpers/colors';
import { CustomButton } from '../../../shared/uiComponents';
import { errorNormalizer } from '../../../shared/Helpers/functions';
import { useRefreshProviderCancellations } from '../../../shared/Helpers/hooks';
import { CancellingParty, RescheduleIntent } from '../../../redux/API/ClientAPIHelpers/scheduleCancellation';
import StatusChip, { StatusChipPopoverList } from './StatusChip';
import { useVisibleMonthFilter } from './MyCancellationStatusChip';

const cancellingPartyLabels: Record<CancellingParty, string> = {
	[CancellingParty.Provider]: 'BT (Provider)',
	[CancellingParty.ClientFamily]: 'Client/Family',
	[CancellingParty.Agency]: 'Agency',
};

const rescheduleIntentLabels: Record<RescheduleIntent, string> = {
	[RescheduleIntent.Yes]: 'Yes - will coordinate a make-up session',
	[RescheduleIntent.No]: 'No - this session will not be made up',
	[RescheduleIntent.NotSure]: 'Not sure yet',
};

const MyPendingRequestsChip: FC = () => {
	const dispatch = useDispatch<DispatchProperties>();

	const role = useSelector((state) => state.account.role);
	const pendingRequests = useSelector((state) => state.scheduleCancellation.pendingRequests);
	const userId = useSelector((state) => state.account.user.id);
	const filteredTherapist = useSelector((state) => state.calendarHelper.filterData.therapist);
	const isAdmin = role?.section.id === AdminTypes.admin;
	const refreshCancellations = useRefreshProviderCancellations();

	useEffect(() => {
		if (userId) {
			dispatch(getPendingRequests(undefined));
		}
	}, [dispatch, userId]);

	const targetProviderId = isAdmin ? filteredTherapist?.id : userId;
	const providerRequests = pendingRequests.filter((r) => r.providerId === targetProviderId);
	const myRequests = useVisibleMonthFilter(providerRequests);
	const count = myRequests.length;

	if (!targetProviderId || count === 0) return null;

	const earliest = myRequests.reduce((a, b) => (a.startDate < b.startDate ? a : b));
	const dateRange = `${dayjs(earliest.startDate).format('MMM D')} \u2013 ${dayjs(earliest.endDate).format('MMM D')}`;

	const canWithdraw = userId === targetProviderId;

	const handleWithdraw = (groupId: string) => {
		dispatch(deleteCancellationGroup(groupId))
			.then(unwrapResult)
			.then(() => refreshCancellations())
			.catch(errorNormalizer);
	};

	const items = myRequests.map((r) => ({
		key: r.groupId,
		title: r.reasonName || 'Cancellation',
		subtitle: `${dayjs(r.startDate).format('MMM D')} \u2013 ${dayjs(r.endDate).format('MMM D')} \u2022 Pending`,
		dotColor: ORANGE.BASE,
		extra: [
			`Cancelling party: ${r.cancellingParty !== null ? cancellingPartyLabels[r.cancellingParty] : 'Unknown'}`,
			`Reschedule intent: ${r.rescheduleIntent !== null ? rescheduleIntentLabels[r.rescheduleIntent] : 'Not specified'}`,
			...(r.notes ? [`Notes: "${r.notes}"`] : []),
		],
	}));

	return (
		<StatusChip
			count={count}
			label={`request${count === 1 ? '' : 's'} pending`}
			subtitle={dateRange}
			colorScheme={{ background: ORANGE.QUATERNARY, text: ORANGE.DARK[16], border: ORANGE.LIGHT[48] }}
			popoverContent={
				<StatusChipPopoverList
					heading={`${count} Pending Request${count === 1 ? '' : 's'}`}
					items={items}
					action={canWithdraw ? (key) => (
						<CustomButton
							title="Withdraw"
							buttonType="tertiary"
							onClick={() => handleWithdraw(key)}
							style={{ color: '#ef4444', borderColor: '#ef4444', borderWidth: '1px', borderStyle: 'solid', fontSize: '12px', padding: '6px 20px', minWidth: 'unset', flexShrink: 0 }}
						/>
					) : undefined}
				/>
			}
		/>
	);
};

export default MyPendingRequestsChip;
